/**
 * callbackRef.js - resolve the payment reference carried by Adumo's callback page.
 *
 * Used by the hosted-card iframe fallback: api/return.php breaks out of the iframe with a
 * PAYMENT_CALLBACK_PAGE postMessage, and App.js has to decide which reference to put on the
 * receipt. Everything else about that payload is Adumo's, undocumented, and has changed
 * shape before, so the scan below is deliberately widened rather than keyed to field names
 * we happen to have seen.
 *
 * Split out as a pure module ON PURPOSE. The bug this guards lived inside App.js's
 * postMessage handler, which reported a hardcoded '', and a mutation test proved no test
 * could see it, because reaching that handler needs the whole Adumo SDK mounted. Keeping the
 * decision here makes it verifiable without a browser. See callbackRef.test.js.
 *
 * Lifted verbatim out of the retired src/googlepay.js when Google Pay moved onto Adumo's
 * own SDK element. Nothing else from that module survived.
 */

const normKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, '');

/** Non-empty string or finite number: the only value shapes that can carry an id. */
function isScalarValue(v) {
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return Number.isFinite(v);
  return false;
}

/** Payload entries as [{ key: normalised, value }], alphabetical by normalised key so
 *  every tier scan below is deterministic regardless of object key order. */
function scalarEntries(p) {
  return Object.keys(p)
    .map((k) => ({ key: normKey(k), value: p[k] }))
    .filter((e) => e.key !== '' && isScalarValue(e.value))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/* Reference detection, priority order (first tier with a hit wins; alphabetical
 * within a tier):
 *   1. exact:    transactionIndex > transactionId > puid > paymentId > orderId
 *                > reference > merchantReference > uuid   (underscores/case ignored)
 *   2. contains: same names as substrings ('gatewayTransactionId', 'txnReference'…)
 *   3. any other key ending 'id'
 * Keys containing 'error' never count as a reference (an errorId is not payment
 * evidence), and English words that merely end in "id" are excluded from tier 3. */
const REF_EXACT_TIER = [
  'transactionindex', 'transactionid', 'puid', 'paymentid', 'orderid',
  'reference', 'merchantreference', 'uuid',
];
const REF_CONTAINS_TIER = [
  'transactionindex', 'transactionid', 'merchantreference', 'paymentid',
  'orderid', 'reference', 'puid', 'uuid',
];
const REF_ENDSWITH_ID_DENY = new Set(['valid', 'invalid', 'paid', 'unpaid', 'void', 'avoid']);

export function extractReference(payload) {
  const entries = scalarEntries(payload || {}).filter((e) => !e.key.includes('error'));
  for (const name of REF_EXACT_TIER) {
    const hit = entries.find((e) => e.key === name);
    if (hit) return String(hit.value).trim();
  }
  for (const name of REF_CONTAINS_TIER) {
    const hit = entries.find((e) => e.key.includes(name));
    if (hit) return String(hit.value).trim();
  }
  const hit = entries.find((e) => e.key.endsWith('id') && !REF_ENDSWITH_ID_DENY.has(e.key));
  return hit ? String(hit.value).trim() : '';
}

/**
 * Resolve the reference to report once the hosted card leg completes.
 *
 * `data` is the postMessage payload from the gateway's callback page (untrusted: read only
 * the reference fields). `pending` is the reference captured when the leg started. Prefer
 * the fresher callback value; fall back to what we already held.
 */
export function resolveCallbackReference(data, pending) {
  const fromCallback = extractReference(data);
  return fromCallback || String(pending || '').trim();
}
